import React, { useState, useEffect } from 'react';
import { Plus, Tent } from 'lucide-react';
import { motion } from 'framer-motion';
import { AddCampTableModal } from '../components/campAccommodation/AddCampTableModal';
import { CampAccommodationTable } from '../components/campAccommodation/CampAccommodationTable';
import { useCampAccommodationStore } from '../stores/campAccommodationStore';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { ErrorMessage } from '../components/common/ErrorMessage';
import { useLanguage } from '../contexts/LanguageContext';

export const CampAccommodationPage = () => {
  const { translations } = useLanguage();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { tables, loading, error, fetchTables } = useCampAccommodationStore();
  const userRole = localStorage.getItem('userRole');
  const isEmployee = userRole === 'admin';

  useEffect(() => {
    fetchTables();
  }, [fetchTables]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {translations?.campAccommodation?.header?.title ||
              'Camp Accommodation'}
          </h1>
          <p className="text-gray-500 mt-1">
            {translations?.campAccommodation?.header?.subtitle ||
              'Manage pilgrim accommodation in Mina and Arafat camps'}
          </p>
        </div>

        {isEmployee && (
          <button
            onClick={() => setIsAddModalOpen(true)}
            className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 
                     transition-colors flex items-center gap-2"
          >
            <Plus size={20} />
            <span>
              {translations?.campAccommodation?.addTableButton ||
                'Add Accommodation Table'}
            </span>
          </button>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-primary-50 rounded-lg">
              <Tent className="text-primary-500" size={20} />
            </div>
            <h3 className="font-medium">
              {translations?.campAccommodation?.stats?.totalCamps ||
                'Total Camps'}
            </h3>
          </div>
          <p className="text-2xl font-bold text-primary-600">
            {Object.keys(tables).length}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-50 rounded-lg">
              <Tent className="text-green-500" size={20} />
            </div>
            <h3 className="font-medium">
              {translations?.campAccommodation?.stats?.occupiedTents ||
                'Occupied Tents'}
            </h3>
          </div>
          <p className="text-2xl font-bold text-green-600">
            {Object.values(tables).reduce(
              (acc, table) =>
                acc +
                table.tents.filter((tent) => tent.occupiedCapacity > 0).length,
              0
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Tent className="text-blue-500" size={20} />
            </div>
            <h3 className="font-medium">
              {translations?.campAccommodation?.stats?.totalCapacity ||
                'Total Capacity'}
            </h3>
          </div>
          <p className="text-2xl font-bold text-blue-600">
            {Object.values(tables).reduce(
              (acc, table) => acc + table.totalCapacity,
              0
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white p-6 rounded-xl shadow-sm"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-yellow-50 rounded-lg">
              <Tent className="text-yellow-500" size={20} />
            </div>
            <h3 className="font-medium">
              {translations?.campAccommodation?.stats?.occupancyRate ||
                'Occupancy Rate'}
            </h3>
          </div>
          <p className="text-2xl font-bold text-yellow-600">
            {Math.round(
              (Object.values(tables).reduce(
                (acc, table) => acc + table.occupiedCapacity,
                0
              ) /
                Object.values(tables).reduce(
                  (acc, table) => acc + table.totalCapacity,
                  0
                )) *
                100 || 0
            )}
            %
          </p>
        </motion.div>
      </div>

      {/* Accommodation Tables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {Object.values(tables).map((table) => (
          <CampAccommodationTable key={table.id} table={table} />
        ))}
      </div>

      {/* No Tables Message */}
      {Object.keys(tables).length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg">
          <p className="text-gray-500">
            {translations?.campAccommodation?.noTablesMessage ||
              'No accommodation tables available'}
          </p>
        </div>
      )}

      {/* Add Table Modal */}
      <AddCampTableModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
      />
    </div>
  );
};
